// Reliable transport for mic telemetry: in-memory queue mirrored to
// localStorage, keepalive fetch, sendBeacon on pagehide, and carryover flush
// on next load. Survives component unmount and (via beacon/carryover) most
// page exits. Observes only — never holds streams or contexts.

import host from "../global";
import { getSessionId, getCachedContext } from "./environment";

const QUEUE_KEY = "mic_log_queue_v2";
const ENDPOINT = `${host}/api/mic-test-logs`;
const BATCH_ENDPOINT = `${host}/api/mic-test-logs/batch`;

let queue = [];
let seq = 0;
let flushing = false;
let listenersArmed = false;
let contextSent = false;

const persist = () => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (_) {}
};

const armPagehide = () => {
  if (listenersArmed) return;
  listenersArmed = true;
  window.addEventListener("pagehide", () => {
    if (queue.length && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        BATCH_ENDPOINT,
        new Blob([JSON.stringify(queue)], { type: "application/json" })
      );
      if (ok) {
        queue = [];
        persist();
      }
    }
  });
};

const flush = async () => {
  if (flushing) return;
  flushing = true;
  let sent = 0;
  try {
    while (queue.length) {
      const record = queue[0];
      let ok = false;
      try {
        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(record),
          keepalive: true,
        });
        ok = res.ok;
      } catch (_) {
        ok = false;
      }
      if (!ok) break; // server unreachable — retry on next enqueue/load
      queue.shift();
      sent++;
    }
  } finally {
    // persist once per flush, not per record — a crash mid-flush only
    // resends records the server dedups on record_id
    if (sent) persist();
    flushing = false;
  }
};

const newRecordId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const enqueueMicLog = (record) => {
  armPagehide();
  const entry = {
    ...record,
    // Stamped once at enqueue: the server dedups on it, so beacon/fetch
    // double-delivery and carryover re-sends can never store twice —
    // covers every record type (answer_health has no attempt_id).
    record_id: newRecordId(),
    session_id: getSessionId(),
    client_seq: ++seq,
  };
  if (!contextSent) {
    const ctx = getCachedContext();
    if (ctx) {
      entry.session_context = ctx;
      contextSent = true;
    }
  }
  queue.push(entry);
  persist();
  void flush();
};

// Ship anything a previous session stranded (replaces the old dead-end
// localStorage fallback that nothing ever read back).
export const flushCarryover = () => {
  armPagehide();
  try {
    const saved = localStorage.getItem(QUEUE_KEY);
    if (saved) {
      const stranded = JSON.parse(saved);
      if (Array.isArray(stranded) && stranded.length) {
        queue = stranded.concat(queue);
      }
    }
  } catch (_) {}
  void flush();
};
